import type { GreeningAnalysis } from '../../services/greeningApi';
import { GREENING_AGENT_PHRASE, GREENING_AGENT_PHRASE_CAP } from './constants';

const ACTIVE = new Set(['pending', 'processing', 'retrying']);
const ERROR = new Set(['failed', 'blocked_by_security', 'insufficient_context']);

export type GreeningChatBlockKind =
  | 'loading'
  | 'primary_active'
  | 'primary_error'
  | 'five_whys_active'
  | 'five_whys_error'
  | 'ready';

export interface GreeningChatAvailability {
  kind: GreeningChatBlockKind;
  canAsk: boolean;
  title: string;
  message: string;
  severity: 'info' | 'warning' | 'error';
  fabLabel: string;
  fabHint?: string;
}

export function getGreeningChatAvailability(
  analysis: GreeningAnalysis | undefined,
  analysisLoading: boolean,
): GreeningChatAvailability {
  if (analysisLoading && !analysis) {
    return {
      kind: 'loading',
      canAsk: false,
      title: 'Verificando análise',
      message: `Aguarde enquanto verificamos o status d${GREENING_AGENT_PHRASE} para esta norma.`,
      severity: 'info',
      fabLabel: 'Chat',
    };
  }

  const status = analysis?.status;
  const fiveWhysStatus = analysis?.five_whys_status;

  if (!status || ACTIVE.has(status)) {
    return {
      kind: 'primary_active',
      canAsk: false,
      title: `${GREENING_AGENT_PHRASE_CAP} está analisando esta norma`,
      message:
        analysis?.user_message ||
        `${GREENING_AGENT_PHRASE_CAP} está processando a análise primária. Assim que terminar, você poderá fazer perguntas sobre este documento. Por favor, retorne mais tarde.`,
      severity: 'info',
      fabLabel: 'Analisando…',
      fabHint: 'Aguarde a análise primária',
    };
  }

  if (ERROR.has(status)) {
    return {
      kind: 'primary_error',
      canAsk: false,
      title: 'Análise indisponível',
      message:
        analysis?.error_message ||
        analysis?.user_message ||
        'Não foi possível concluir a análise desta norma. Tente novamente mais tarde ou contate o suporte.',
      severity: 'error',
      fabLabel: 'Indisponível',
      fabHint: 'Análise não concluída',
    };
  }

  if (status === 'completed') {
    if (!fiveWhysStatus || ACTIVE.has(String(fiveWhysStatus))) {
      return {
        kind: 'five_whys_active',
        canAsk: false,
        title: `${GREENING_AGENT_PHRASE_CAP} ainda está estudando esta norma`,
        message:
          analysis?.five_whys_message ||
          `${GREENING_AGENT_PHRASE_CAP} está concluindo o aprofundamento (5 Porquês) antes de responder perguntas. Assim que terminar, você poderá conversar sobre este documento. Por favor, retorne mais tarde.`,
        severity: 'info',
        fabLabel: 'Estudando…',
        fabHint: 'Aprofundamento em andamento',
      };
    }

    if (ERROR.has(String(fiveWhysStatus))) {
      return {
        kind: 'five_whys_error',
        canAsk: false,
        title: 'Aprofundamento indisponível',
        message:
          analysis?.five_whys_message ||
          'Não foi possível concluir o aprofundamento (5 Porquês) desta norma. Tente novamente mais tarde.',
        severity: 'error',
        fabLabel: 'Indisponível',
        fabHint: 'Aprofundamento não concluído',
      };
    }
  }

  return {
    kind: 'ready',
    canAsk: true,
    title: '',
    message: '',
    severity: 'info',
    fabLabel: 'Chat',
    fabHint: 'Perguntas liberadas',
  };
}
